import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import api from "../utils/Api";



/**
 * SubscriptionContext
 *
 * Fuente de verdad global del estado de suscripción de la empresa.
 * Se consulta una única vez al autenticar el usuario (cambio de `token`)
 * y puede refrescarse llamando a `refresh()` (p.ej. tras activar la suscripción).
 *
 * Valores expuestos:
 *   subscribed {boolean|null}  — null mientras carga, true/false tras la consulta
 *   loading    {boolean}       — true durante la petición inicial
 *   refresh    {function}      — vuelve a consultar /subscribe/check
 */



export const SubscriptionContext = createContext({
  subscribed: null,
  loading:    true,
  refresh:    () => {},
});

export function SubscriptionProvider({ children }) {
  const { token } = useContext(AuthContext);

  const [subscribed, setSubscribed] = useState(null);
  const [loading,    setLoading]    = useState(true);

  const check = useCallback(async () => {
    // Feature flag: si Stripe está desactivado todos tienen acceso completo
    if (process.env.REACT_APP_STRIPE_ENABLED === "false") {
      setSubscribed(true);
      setLoading(false);
      return;
    }

    if (!token) {
      setSubscribed(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Consulta local-only (sin refresh): resuelve acceso desde BD, sin tocar Stripe.
      const res = await api.get("/subscribe/check", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscribed(!!res.data.subscribed);
    } catch {
      // S-08: ante un error de red transitorio (timeout, 503…) NO forzamos
      // subscribed=false porque bloquearía a usuarios activos y pagados.
      // Mantenemos el estado anterior; el usuario puede recargar si lo necesita.
      // Solo reseteamos a null en el logout (token=null, rama de arriba).
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Refrescar cada vez que el token cambia (login / logout)
  useEffect(() => {
    check();
  }, [check]);

  return (
    <SubscriptionContext.Provider value={{ subscribed, loading, refresh: check }}>
      {children}
    </SubscriptionContext.Provider>
  );
}